<template>
  <div class="server-commands-section">
    <div class="section-header">
      <h2>Server</h2>
    </div>
    <div class="commands-content">
      <div class="command-item">
        <div class="command-info">
          <h3>Refresh Cache</h3>
          <p>Updates the server cache with the latest AOS data from the repository.</p>
        </div>
        <button :disabled="refreshingCache" class="refresh-cache-btn" @click="refreshCache">
          {{ refreshingCache ? 'Refreshing...' : 'Refresh Cache' }}
        </button>
        <div
          v-if="cacheRefreshMessage"
          :class="[
            'cache-message',
            cacheRefreshMessage.includes('Failed') ? 'cache-error' : 'cache-success',
          ]"
        >
          {{ cacheRefreshMessage }}
        </div>
      </div>

      <div class="command-item">
        <div class="command-info">
          <h3>Inspect Data</h3>
          <p>View the raw JSON content from the blob endpoint for debugging and inspection.</p>
        </div>
        <button class="refresh-cache-btn" @click="openDataModal">Inspect Data</button>
      </div>

      <h3>Server Manifest</h3>
      <div v-if="manifestError" class="cache-message cache-error">
        {{ manifestError }}
      </div>
      <div v-else-if="loadingManifest" class="loading-message">Loading manifest...</div>
      <div v-else-if="manifestData" class="manifest-content">
        <div class="manifest-grid">
          <div v-for="(entry, key) in manifestData" :key="key" class="manifest-item">
            <div class="manifest-name">{{ entry.name }}</div>
            <div class="manifest-details">
              <div class="manifest-type">
                <strong>Type:</strong> {{ getManifestTypeName(entry.type) }}
              </div>
              <div class="manifest-sha"><strong>SHA:</strong> {{ entry.sha }}</div>
              <div class="manifest-version">
                <strong>Server Version:</strong> {{ entry.serverVersion }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Blob Inspector Modal -->
  <BlobInspector :show="showDataModal" @close="closeDataModal" />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { client, parseErrorMessage, type ServerManifest } from '../client.js';
import BlobInspector from './BlobInspector.vue';

const refreshingCache = ref(false);
const cacheRefreshMessage = ref<string | null>(null);
const loadingManifest = ref(false);
const manifestData = ref<ServerManifest | null>(null);
const manifestError = ref<string | null>(null);

// Modal state for BlobInspector
const showDataModal = ref(false);

const refreshCache = async () => {
  if (!client.value) return;

  refreshingCache.value = true;
  cacheRefreshMessage.value = null;

  try {
    await client.value.refreshServerCache();
    cacheRefreshMessage.value = 'Cache refreshed successfully!';
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      cacheRefreshMessage.value = null;
    }, 3000);
    // Reload manifest after cache refresh
    await loadManifest();
  } catch (err: any) {
    cacheRefreshMessage.value = `Failed to refresh cache: ${parseErrorMessage(err)}`;
    console.error('Error refreshing cache:', err);
  } finally {
    refreshingCache.value = false;
  }
};

const loadManifest = async () => {
  if (!client.value) return;

  loadingManifest.value = true;
  manifestError.value = null;

  try {
    manifestData.value = await client.value.getManifest();
  } catch (err: any) {
    manifestError.value = `Failed to load manifest: ${parseErrorMessage(err)}`;
    console.error('Error loading manifest:', err);
  } finally {
    loadingManifest.value = false;
  }
};

const openDataModal = () => {
  showDataModal.value = true;
};

const closeDataModal = () => {
  showDataModal.value = false;
};

const getManifestTypeName = (type: number): string => {
  switch (type) {
    case 0:
      return 'Branch';
    case 1:
      return 'Release';
    default:
      return 'Unknown';
  }
};

onMounted(() => {
  loadManifest();
});
</script>

<style scoped>
.server-commands-section {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.section-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  color: #1f2937;
}

.commands-content {
  padding: 1rem;
}

.command-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.command-info h3 {
  margin: 0 0 0.25rem 0;
  color: #1f2937;
  font-size: 1rem;
}

.command-info p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.refresh-cache-btn {
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  white-space: nowrap;
}

.refresh-cache-btn:hover:not(:disabled) {
  background: #059669;
}

.refresh-cache-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.cache-message {
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 500;
}

.cache-success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.cache-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.loading-message {
  padding: 1rem;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

.manifest-content {
  padding-top: 0.5rem;
}

.manifest-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.manifest-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 1rem;
}

.manifest-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.manifest-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.manifest-type,
.manifest-sha,
.manifest-version {
  font-size: 0.75rem;
  color: #6b7280;
}

.manifest-type strong,
.manifest-sha strong,
.manifest-version strong {
  color: #374151;
}
</style>
